import { useMemo, useState } from 'react';
import {
  AlertTriangle,
  CalendarClock,
  ChevronRight,
  GripVertical,
  MessageCircle,
  PhoneOff,
  UserRound,
} from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { STATUS_LABELS, type AlunoCasoBuscaAtiva } from '@/domains';
import { cn } from '@/lib/utils';
import { RegistrarContatoModal } from './RegistrarContatoModal';

interface AlunoBuscaCardProps {
  aluno: AlunoCasoBuscaAtiva;
  escolaId: string;
  escolaNome?: string;
  onContactRegistered: (novoRegistro?: Record<string, unknown>, casoAtualizado?: any) => void;
  onOpenDetails: () => void;
  onWhatsAppOpened: (templateId: string, message: string) => Promise<void>;
  innerRef?: (element?: HTMLElement | null) => void;
  draggableProps?: any;
  dragHandleProps?: any;
}

const templates = [
  {
    id: 'ACOLHIMENTO_INICIAL',
    nome: 'Acolhimento inicial',
    texto:
      'Olá, {responsavel}. Aqui é da equipe da {escola}. Percebemos que {aluno} não conseguiu participar das últimas aulas e queremos saber como vocês estão. Há alguma dificuldade em que a escola possa ajudar? Se for mais fácil, pode responder por áudio.',
  },
  {
    id: 'ESCUTA_BARREIRA',
    nome: 'Entender a dificuldade',
    texto:
      'Oi, {responsavel}. Passando para acompanhar {aluno}. Nossa intenção é apoiar, não cobrar. Alguma destas situações está dificultando a frequência: saúde, transporte, rotina de trabalho, dificuldade nas aulas ou outra situação? Se preferir, pode contar com suas próprias palavras.',
  },
  {
    id: 'PLANO_RETORNO',
    nome: 'Confirmar plano de retorno',
    texto:
      'Obrigado por nos contar, {responsavel}. Queremos combinar com vocês um caminho seguro para o retorno de {aluno}. A escola continuará acompanhando e pode ajudar a organizar os próximos passos. Qual data seria possível para conversarmos ou planejarmos esse retorno?',
  },
] as const;

const riskStyles = {
  CRITICO: 'border-l-rose-500',
  ALTO: 'border-l-orange-500',
  MEDIO: 'border-l-amber-400',
  OBSERVACAO: 'border-l-sky-500',
};

const riskBadgeStyles = {
  CRITICO: 'border-rose-200 bg-rose-50 text-rose-700',
  ALTO: 'border-orange-200 bg-orange-50 text-orange-700',
  MEDIO: 'border-amber-200 bg-amber-50 text-amber-700',
  OBSERVACAO: 'border-sky-200 bg-sky-50 text-sky-700',
};

export function AlunoBuscaCard({
  aluno,
  escolaId,
  escolaNome = 'escola',
  onContactRegistered,
  onOpenDetails,
  onWhatsAppOpened,
  innerRef,
  draggableProps,
  dragHandleProps,
}: AlunoBuscaCardProps) {
  const [messageOpen, setMessageOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(templates[0].id);
  const [message, setMessage] = useState('');

  const iniciais = aluno.aluno_nome
    .split(' ')
    .slice(0, 2)
    .map(nome => nome[0])
    .join('')
    .toUpperCase();

  const numeroLimpo = aluno.telefone?.replace(/\D/g, '') || '';
  const telefoneValido = numeroLimpo.length >= 10;
  const numeroWhatsApp = numeroLimpo.startsWith('55') ? numeroLimpo : `55${numeroLimpo}`;

  const renderTemplate = (templateId: string) => {
    const template = templates.find(item => item.id === templateId) || templates[0];
    return template.texto
      .replaceAll('{responsavel}', aluno.nome_responsavel || 'responsável')
      .replaceAll('{aluno}', aluno.aluno_nome)
      .replaceAll('{escola}', escolaNome);
  };

  const openMessage = (event: React.MouseEvent) => {
    event.stopPropagation();
    const initial = templates[0].id;
    setSelectedTemplate(initial);
    setMessage(renderTemplate(initial));
    setMessageOpen(true);
  };

  const changeTemplate = (templateId: string) => {
    setSelectedTemplate(templateId);
    setMessage(renderTemplate(templateId));
  };

  const sendToWhatsApp = async () => {
    if (!telefoneValido) return;
    window.open(
      `https://wa.me/${numeroWhatsApp}?text=${encodeURIComponent(message)}`,
      '_blank',
      'noopener,noreferrer',
    );
    await onWhatsAppOpened(selectedTemplate, message);
    setMessageOpen(false);
  };

  const followUpLabel = useMemo(() => {
    if (!aluno.caso.proximo_follow_up) return null;
    return new Date(aluno.caso.proximo_follow_up).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  }, [aluno.caso.proximo_follow_up]);

  return (
    <>
      <Card
        ref={innerRef}
        {...draggableProps}
        onClick={onOpenDetails}
        className={cn(
          'w-full cursor-pointer overflow-hidden border border-slate-200 border-l-4 bg-white shadow-none transition hover:border-slate-300 hover:shadow-md',
          riskStyles[aluno.nivel],
        )}
      >
        <CardContent className="p-4">
          <div className="flex gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-900 text-sm font-bold text-white">
              {iniciais}
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <h3 className="truncate text-sm font-semibold text-slate-950" title={aluno.aluno_nome}>
                    {aluno.aluno_nome}
                  </h3>
                  <p className="truncate text-xs text-slate-500">{aluno.turma_nome}</p>
                </div>
                <button
                  {...dragHandleProps}
                  type="button"
                  className="-mr-2 -mt-2 min-h-9 min-w-9 rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                  aria-label={`Mover caso de ${aluno.aluno_nome}`}
                  onClick={event => event.stopPropagation()}
                >
                  <GripVertical className="h-4 w-4" />
                </button>
              </div>

              <div className="mt-2 flex flex-wrap gap-1.5">
                <Badge variant="outline" className={cn('text-[10px]', riskBadgeStyles[aluno.nivel])}>
                  Risco {aluno.score} · {aluno.nivel.toLowerCase()}
                </Badge>
                {aluno.followUpVencido && (
                  <Badge variant="outline" className="border-rose-200 bg-rose-50 text-[10px] text-rose-700">
                    <AlertTriangle className="mr-1 h-3 w-3" /> Follow-up vencido
                  </Badge>
                )}
              </div>
            </div>
          </div>

          <div className="mt-3 rounded-lg bg-slate-50 p-2.5">
            <p className="line-clamp-2 text-xs font-medium text-slate-700">
              {aluno.motivos.slice(0, 2).join(' · ')}
            </p>
            <p className="mt-1 text-[11px] text-slate-500">
              {STATUS_LABELS[aluno.caso.status]}
              {aluno.resumo?.ultimoContato
                ? ` · último contato ${new Date(aluno.resumo.ultimoContato).toLocaleDateString('pt-BR')}`
                : ' · nenhum contato'}
            </p>
          </div>

          {(aluno.caso.responsavel_nome || followUpLabel) && (
            <div className="mt-3 grid gap-1 text-[11px] text-slate-600">
              {aluno.caso.responsavel_nome && (
                <span className="flex items-center gap-1.5">
                  <UserRound className="h-3.5 w-3.5 text-slate-400" />
                  {aluno.caso.responsavel_nome}
                </span>
              )}
              {followUpLabel && (
                <span className={cn('flex items-center gap-1.5', aluno.followUpVencido && 'font-semibold text-rose-700')}>
                  <CalendarClock className="h-3.5 w-3.5" />
                  {followUpLabel}
                </span>
              )}
            </div>
          )}

          <div className="mt-4 flex gap-2 border-t border-slate-100 pt-3">
            {telefoneValido ? (
              <Button
                size="sm"
                variant="outline"
                className="min-h-9 flex-1 border-emerald-200 bg-emerald-50 text-xs text-emerald-700 hover:bg-emerald-100"
                onClick={openMessage}
              >
                <MessageCircle className="mr-1.5 h-3.5 w-3.5" />
                Mensagem
              </Button>
            ) : (
              <Button size="sm" variant="outline" disabled className="min-h-9 flex-1 text-xs">
                <PhoneOff className="mr-1.5 h-3.5 w-3.5" />
                Sem número
              </Button>
            )}

            <div onClick={event => event.stopPropagation()}>
              <RegistrarContatoModal
                alunoId={aluno.aluno_id}
                nomeAluno={aluno.aluno_nome}
                escolaId={escolaId}
                caso={aluno.caso}
                onSuccess={onContactRegistered}
              />
            </div>
          </div>

          <button
            type="button"
            className="mt-3 flex min-h-8 w-full items-center justify-center gap-1 text-xs font-medium text-indigo-700"
            onClick={event => {
              event.stopPropagation();
              onOpenDetails();
            }}
          >
            Ver contexto completo <ChevronRight className="h-3.5 w-3.5" />
          </button>
        </CardContent>
      </Card>

      <Dialog open={messageOpen} onOpenChange={setMessageOpen}>
        <DialogContent className="sm:max-w-[620px]">
          <DialogHeader>
            <DialogTitle>Mensagem acolhedora</DialogTitle>
            <DialogDescription>
              Escolha uma abordagem e revise o texto antes de abrir o WhatsApp.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-3">
            <div className="grid gap-2 sm:grid-cols-3">
              {templates.map(template => (
                <button
                  key={template.id}
                  type="button"
                  onClick={() => changeTemplate(template.id)}
                  className={cn(
                    'min-h-12 rounded-lg border px-3 py-2 text-left text-xs font-medium transition',
                    selectedTemplate === template.id
                      ? 'border-indigo-300 bg-indigo-50 text-indigo-800'
                      : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50',
                  )}
                >
                  {template.nome}
                </button>
              ))}
            </div>
            <Textarea value={message} onChange={event => setMessage(event.target.value)} rows={7} />
            <p className="text-xs text-slate-500">
              Abrir o WhatsApp será registrado como tentativa, não como mensagem entregue.
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setMessageOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={sendToWhatsApp} disabled={!message.trim()} className="bg-emerald-600 hover:bg-emerald-700">
              <MessageCircle className="mr-2 h-4 w-4" />
              Abrir WhatsApp
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
